/**
 * Power Level Calculation System
 *
 * Calculates player Power Level (PL) based on:
 * - Base power (player level * 10)
 * - Equipped skills (tier + upgrade level)
 * - Equipped artifacts (FIXED power level bonus by rarity: common +150, uncommon +300, rare +500, epic +700, legendary +900)
 * - Manifest ascension bonus
 */

import { getPowerLevelBonusForRarity } from '../constants/artifactRarity';

export interface PowerBreakdown {
  base: number;
  skills: number;
  artifacts: number;
  ascension: number;
  total: number;
}

export interface PowerLevelResult {
  powerLevel: number;
  breakdown: PowerBreakdown;
}

/** Largest PL sources (base, skills, artifacts, ascension) for profile / tooltips. */
export function topPowerLevelContributors(
  breakdown: Partial<Pick<PowerBreakdown, 'base' | 'skills' | 'artifacts' | 'ascension'>> | null | undefined,
  topN = 3
): Array<{ label: string; value: number }> {
  if (!breakdown) return [];
  const rows = [
    { label: 'Base (level × 10)', value: Math.max(0, Math.floor(Number(breakdown.base) || 0)) },
    { label: 'Equipped skills', value: Math.max(0, Math.floor(Number(breakdown.skills) || 0)) },
    { label: 'Equipped artifacts', value: Math.max(0, Math.floor(Number(breakdown.artifacts) || 0)) },
    { label: 'Manifest ascension', value: Math.max(0, Math.floor(Number(breakdown.ascension) || 0)) },
  ];
  return [...rows].sort((a, b) => b.value - a.value).slice(0, Math.max(1, topN));
}

/**
 * Tier value map for skills
 */
const SKILL_TIER_VALUES: Record<number, number> = {
  1: 15,
  2: 30,
  3: 50,
  4: 75
};

/**
 * Skill upgrade multiplier map
 */
const SKILL_UPGRADE_MULTIPLIERS: Record<number, number> = {
  1: 1.0,
  2: 1.15,
  3: 1.35,
  4: 1.6,
  5: 1.9
};

/**
 * Manifest ascension bonus map
 */
const ASCENSION_BONUS: Record<number, number> = {
  1: 0,
  2: 25,
  3: 60,
  4: 110
};

/**
 * Calculate power contribution from a single skill
 */
export function getSkillPower(skillDoc: any): number {
  if (!skillDoc) return 0;
  
  // Defaults for missing fields
  const tier = skillDoc.tier || 1;
  const upgradeLevel = skillDoc.upgradeLevel || skillDoc.level || 1;
  
  // Ensure tier and upgradeLevel are valid
  const validTier = Math.max(1, Math.min(4, tier));
  const validUpgradeLevel = Math.max(1, Math.min(5, upgradeLevel));
  
  const tierValue = SKILL_TIER_VALUES[validTier] || SKILL_TIER_VALUES[1];
  const multiplier = SKILL_UPGRADE_MULTIPLIERS[validUpgradeLevel] || SKILL_UPGRADE_MULTIPLIERS[1];
  
  return Math.round(tierValue * multiplier);
}

/**
 * Calculate power contribution from a single artifact.
 * Uses FIXED Power Level bonus by rarity (common +150 … legendary +900).
 * If artifact has powerLevelBonus set (e.g. from migration), use it; otherwise derive from rarity.
 */
export function getArtifactPower(artifactDoc: any): number {
  if (!artifactDoc) return 0;
  if (typeof artifactDoc.powerLevelBonus === 'number' && artifactDoc.powerLevelBonus >= 0) {
    return artifactDoc.powerLevelBonus;
  }
  const bonus = getPowerLevelBonusForRarity(artifactDoc.rarity);
  return bonus;
}

/**
 * Get ascension bonus from manifest ascension level
 */
export function getAscensionBonus(ascensionLevel: number | null | undefined): number {
  if (!ascensionLevel || ascensionLevel < 1) return ASCENSION_BONUS[1];
  if (ascensionLevel > 4) return ASCENSION_BONUS[4];
  return ASCENSION_BONUS[ascensionLevel] || ASCENSION_BONUS[1];
}

/**
 * Compute total power level from all components
 */
export function computePowerLevel(params: {
  playerLevel: number;
  equippedSkillDocs: any[]; // Array of skill document data
  equippedArtifactDocs: any[]; // Array of artifact document data
  manifestAscensionLevel?: number | null;
}): PowerLevelResult {
  const {
    playerLevel = 1,
    equippedSkillDocs = [],
    equippedArtifactDocs = [],
    manifestAscensionLevel = 1
  } = params;
  
  // Base power: player level * 10
  const base = playerLevel * 10;
  
  // Skill power: sum of all equipped skills
  const skills = equippedSkillDocs.reduce((sum, skillDoc) => {
    return sum + getSkillPower(skillDoc);
  }, 0);
  
  // Artifact power: sum of all equipped artifacts
  const artifacts = equippedArtifactDocs.reduce((sum, artifactDoc) => {
    return sum + getArtifactPower(artifactDoc);
  }, 0);
  
  // Ascension bonus
  const ascension = getAscensionBonus(manifestAscensionLevel);
  
  // Total power level
  const total = base + skills + artifacts + ascension;
  
  return {
    powerLevel: total,
    breakdown: {
      base,
      skills,
      artifacts,
      ascension,
      total
    }
  };
}


